import { Injectable, signal } from '@angular/core';
import {
  BatchDocumentsStoreItem,
  BatchDocumentsStoreOneRun,
  DocumentDownloadResult,
} from './batch-documents-handler';
import { BatchDocumentStatus } from '$modules/personal/modules/batch';

@Injectable({
  providedIn: 'root',
})
export class BatchDocumentsStoreService {
  public readonly loadingBatchDocumentsStore = signal<
    readonly BatchDocumentsStoreOneRun[]
  >([]);

  public readonly loadingBatchAllDocumentsStore = signal<
    readonly BatchDocumentsStoreItem[]
  >([]);

  public findSpecificStoreItem(
    documentInfo: Omit<BatchDocumentsStoreItem, 'documentStatus'>,
    isAllDocuments: boolean
  ): BatchDocumentsStoreItem | undefined {
    if (isAllDocuments) {
      return this.loadingBatchAllDocumentsStore().find(
        (item) => item.batchId === documentInfo.batchId
      );
    } else {
      return [...this.loadingBatchDocumentsStore()]
        .reverse()
        .flat()
        .find(
          (item) =>
            item.batchId === documentInfo.batchId &&
            item.departureId === documentInfo.departureId &&
            item.documentType === documentInfo.documentType
        );
    }
  }

  public pushNewLoadingStoreItem(
    documentInfo: BatchDocumentsStoreItem,
    isAllDocuments: boolean
  ): void {
    if (isAllDocuments) {
      this.loadingBatchAllDocumentsStore.update((items) => [
        ...items.filter((item) => item.batchId !== documentInfo.batchId),
        documentInfo,
      ]);
    } else {
      const documents = this.loadingBatchDocumentsStore();
      const isLastRunProcessing = !!(
        documents[documents.length - 1] || []
      ).some((item) => item.documentStatus === 'processing');

      if (isLastRunProcessing) {
        this.loadingBatchDocumentsStore.update((items) => [
          ...items.slice(0, -1),
          [
            ...(items[items.length - 1] || []).filter(
              (item) =>
                !(
                  item.batchId === documentInfo.batchId &&
                  item.departureId === documentInfo.departureId &&
                  item.documentType === documentInfo.documentType
                )
            ),
            documentInfo,
          ],
        ]);
      } else {
        this.loadingBatchDocumentsStore.update((items) => [
          ...items,
          [documentInfo],
        ]);
      }
    }
  }

  public updateStoreProcessingItem(res: DocumentDownloadResult): void {
    const { documentInfo, errorOccurred, isAllDocuments } = res;

    if (isAllDocuments) {
      this.loadingBatchAllDocumentsStore.update((items) =>
        items.map((item) =>
          this.isProcessingItemMatch(item, documentInfo, isAllDocuments)
            ? {
                ...item,
                documentStatus: errorOccurred
                  ? BatchDocumentStatus.ERROR
                  : BatchDocumentStatus.DONE,
              }
            : item
        )
      );
    } else {
      this.loadingBatchDocumentsStore.update((items) => [
        ...items.slice(0, -1),
        (items[items.length - 1] || []).map((item) =>
          this.isProcessingItemMatch(item, documentInfo, isAllDocuments)
            ? {
                ...item,
                documentStatus: errorOccurred
                  ? BatchDocumentStatus.ERROR
                  : BatchDocumentStatus.DONE,
              }
            : item
        ),
      ]);
    }
  }

  /**
   * Marks failed downloads as done to hide any error messages left for departures.
   */
  public markLastDepartureDocumentsDownloadAsDone(batchId: number): void {
    this.loadingBatchDocumentsStore.update((runs) => {
      if (!runs.length) {
        return runs;
      }

      const lastItemPositionByKey = new Map<
        string,
        {
          readonly runIndex: number;
          readonly itemIndex: number;
          readonly documentStatus: BatchDocumentStatus;
        }
      >();

      runs.forEach((run, runIndex) => {
        run.forEach((item, itemIndex) => {
          if (item.batchId === batchId) {
            const key = `${item.departureId}:${item.documentType}`;
            lastItemPositionByKey.set(key, {
              runIndex,
              itemIndex,
              documentStatus: item.documentStatus,
            });
          }
        });
      });

      const lastDocumentsDownloadInfo = Array.from(
        lastItemPositionByKey.values()
      );
      const positionsToMarkDone = lastDocumentsDownloadInfo.filter(
        (info) => info.documentStatus === BatchDocumentStatus.ERROR
      );

      if (!positionsToMarkDone.length) {
        return runs;
      }

      return runs.map((run, runIndex) =>
        run.map((item, itemIndex) =>
          positionsToMarkDone.find(
            (info) => info.runIndex === runIndex && info.itemIndex === itemIndex
          )
            ? { ...item, documentStatus: BatchDocumentStatus.DONE }
            : item
        )
      );
    });
  }

  private isProcessingItemMatch(
    item: BatchDocumentsStoreItem,
    documentInfo: BatchDocumentsStoreItem,
    isAllDocuments: boolean
  ): boolean {
    if (item.documentStatus !== 'processing') {
      return false;
    }

    if (isAllDocuments) {
      return item.batchId === documentInfo.batchId;
    }

    return (
      item.batchId === documentInfo.batchId &&
      item.departureId === documentInfo.departureId &&
      item.documentType === documentInfo.documentType
    );
  }
}
